/**
 * Boot Manager Module
 * Handles boot sequence display and persistence
 */

// Prevent redeclaration if script is loaded multiple times
if (typeof BootManager === 'undefined') {
    class BootManager {
        constructor() {
            this.bootIntervalDays = 1; // Default: show boot once per day
            this.bootEnabled = true;
        }

        /**
         * Check if boot should be shown
         */
        shouldShowBoot() {
            if (!this.bootEnabled) return false;

            const lastBoot = localStorage.getItem('smash_last_boot_timestamp');
            if (!lastBoot) return true;

            const daysSinceBoot = (Date.now() - parseInt(lastBoot)) / (1000 * 60 * 60 * 24);
            return daysSinceBoot >= this.bootIntervalDays;
        }

        /**
         * Show boot sequence
         */
        showBoot() {
            const bootContainer = document.getElementById('boot-sequence');
            if (!bootContainer) {
                console.warn('BootManager: Boot sequence container not found');
                return;
            }

            // Hide desktop to ensure boot sequence is visible (use class, not inline style)
            const desktop = document.getElementById('desktop');
            if (desktop) {
                desktop.classList.remove('visible');
            }

            // Show boot container (black screen) - use class instead of inline style
            bootContainer.classList.add('visible');
            bootContainer.style.zIndex = '100000';
            document.body.style.overflow = 'hidden';

            // Start boot sequence after 0.2s black screen (reduced for speed)
            setTimeout(() => {
                this.startBootSequence();
            }, 200);
        }

        /**
         * Start boot sequence
         */
        startBootSequence() {
            const header = document.getElementById('boot-header');
            const footer = document.getElementById('boot-footer');
            const textContainer = document.getElementById('boot-text-container');

            if (!header || !footer || !textContainer) {
                console.warn('BootManager: Boot sequence elements not found');
                return;
            }

            // Show header and footer immediately (no animation)
            header.classList.add('st-boot-sequence__header--visible');
            footer.classList.add('st-boot-sequence__footer--visible');

            // Setup DEL button handler
            this.setupDelButton();

            // Start loading sequence after small delay (reduced for speed)
            setTimeout(() => {
                this.runLoadingSequence();
            }, 50);
        }

        /**
         * Run loading sequence
         */
        async runLoadingSequence() {
            const textContainer = document.getElementById('boot-text-container');
            if (!textContainer) return;

            // Step 1: Memory Test with counter
            await this.showMemoryTest();

            // Step 2: BIOS Extension block
            await this.showBiosExtension();

            // Step 3: IDE Detection and Component Initialization lines
            await this.showDetectionLines();
        }

        /**
         * Show Memory Test line with counter animation
         */
        showMemoryTest() {
            return new Promise((resolve) => {
                const textContainer = document.getElementById('boot-text-container');
                const line = document.createElement('div');
                line.className = 'st-boot-sequence__line';

                const prefix = 'Memory Test :  ';
                const suffix = 'K  OK (Faultless)';
                const targetValue = 32768;

                let currentValue = 0;
                const increment = Math.ceil(targetValue / 20); // Complete in ~20 steps
                const cursor = document.createElement('span');
                cursor.className = 'st-boot-sequence__cursor';
                cursor.textContent = '_';

                line.innerHTML = prefix + '<span class="st-boot-sequence__memory-counter">0</span>K' + cursor.outerHTML;
                textContainer.appendChild(line);

                const counter = line.querySelector('.st-boot-sequence__memory-counter');

                const updateCounter = () => {
                    if (currentValue < targetValue) {
                        currentValue = Math.min(currentValue + increment, targetValue);
                        counter.textContent = currentValue.toLocaleString();
                        setTimeout(updateCounter, 15); // Slightly faster but readable
                    } else {
                        // Show OK status and remove cursor
                        line.innerHTML = prefix + '<span class="st-boot-sequence__memory-counter">' + targetValue.toLocaleString() + '</span>K  <span class="st-boot-sequence__line--ok">OK (Faultless)</span>';
                        line.classList.add('st-boot-sequence__line--ok');
                        setTimeout(resolve, 200); // Give time to read
                    }
                };

                updateCounter();
            });
        }

        /**
         * Show BIOS Extension block
         */
        showBiosExtension() {
            return new Promise((resolve) => {
                const textContainer = document.getElementById('boot-text-container');

                const block = document.createElement('div');
                block.style.marginTop = '8px';
                block.style.marginBottom = '8px';

                const line1 = document.createElement('div');
                line1.className = 'st-boot-sequence__line';
                line1.textContent = 'Smash Plug and Play BIOS Extension  v1.0A';

                const line2 = document.createElement('div');
                line2.className = 'st-boot-sequence__line';
                line2.textContent = 'Copyright (C) 2026, Smash Tennis, Inc.';

                block.appendChild(line1);
                block.appendChild(line2);
                textContainer.appendChild(block);

                setTimeout(resolve, 150); // Give time to read BIOS extension
            });
        }

        /**
         * Show detection and initialization lines
         */
        showDetectionLines() {
            // Reduced lines for faster boot (under 2 seconds)
            const lines = [
                { text: 'Detecting IDE Primary Master  ...  SMASHHD (Center Court)', ok: false },
                { text: 'Detecting IDE Primary Slave   ...  SMASHCD (Baseline Drive)', ok: false },
                { text: 'Initializing Racket Controller...  OK (Grip Locked)', ok: true },
                { text: 'Loading Mullet Driver...          OK (Hairband Engaged)', ok: true },
                { text: 'Calibrating Neon Strings...       OK (Tension: 55 lbs)', ok: true },
                { text: 'Booting from SMASHHD...', ok: false }
            ];

            return this.showLinesSequentially(lines, 0);
        }

        /**
         * Show lines sequentially with skip hint
         */
        showLinesSequentially(lines, index) {
            if (index >= lines.length) {
                // Remove cursor from last line before completing
                const textContainer = document.getElementById('boot-text-container');
                const lastLine = textContainer.querySelector('.st-boot-sequence__line:last-child');
                if (lastLine) {
                    const cursor = lastLine.querySelector('.st-boot-sequence__cursor');
                    if (cursor) {
                        cursor.remove();
                    }
                }

                // Boot sequence complete (reduced delay for speed)
                setTimeout(() => {
                    this.completeBootSequence();
                }, 200); // Reduced from 500ms
                return Promise.resolve();
            }

            return new Promise((resolve) => {
                const textContainer = document.getElementById('boot-text-container');
                const lineData = lines[index];

                // Remove cursor from previous line
                if (index > 0) {
                    const previousLines = textContainer.querySelectorAll('.st-boot-sequence__line');
                    if (previousLines.length > 0) {
                        const previousLine = previousLines[previousLines.length - 1];
                        const previousCursor = previousLine.querySelector('.st-boot-sequence__cursor');
                        if (previousCursor) {
                            previousCursor.remove();
                        }
                    }
                }

                if (lineData.text === '') {
                    // Empty line for spacing
                    const spacer = document.createElement('div');
                    spacer.className = 'st-boot-sequence__line';
                    spacer.style.marginBottom = '4px';
                    textContainer.appendChild(spacer);
                    setTimeout(() => {
                        this.showLinesSequentially(lines, index + 1).then(resolve);
                    }, 50);
                    return;
                }

                const line = document.createElement('div');
                line.className = 'st-boot-sequence__line';

                // Extract the part before "..." and after
                const parts = lineData.text.split('...');
                const before = parts[0] + '...';
                const after = parts[1] || '';

                // Show skip hint first (shorter on mobile)
                const skipHint = document.createElement('span');
                skipHint.className = 'st-boot-sequence__line--skip-hint';
                const isMobile = window.innerWidth <= 767;
                skipHint.textContent = isMobile ? ' [F4/touch to skip]' : ' [Press F4 or touch to skip]';

                line.innerHTML = before + skipHint.outerHTML;
                textContainer.appendChild(line);

                // After short delay, replace with actual content
                setTimeout(() => {
                    const cursor = document.createElement('span');
                    cursor.className = 'st-boot-sequence__cursor';
                    cursor.textContent = '_';

                    if (lineData.ok) {
                        // Split OK line to highlight OK part
                        const okParts = after.split('OK');
                        if (okParts.length === 2) {
                            line.innerHTML = before + '  <span class="st-boot-sequence__line--ok">OK' + okParts[1] + '</span>' + cursor.outerHTML;
                            line.classList.add('st-boot-sequence__line--ok');
                        } else {
                            line.innerHTML = before + '  <span class="st-boot-sequence__line--ok">' + after + '</span>' + cursor.outerHTML;
                            line.classList.add('st-boot-sequence__line--ok');
                        }
                    } else {
                        line.innerHTML = before + '  ' + after + cursor.outerHTML;
                    }

                    // Continue to next line (give time to read each line)
                    setTimeout(() => {
                        this.showLinesSequentially(lines, index + 1).then(resolve);
                    }, 100); // Time to read each line
                }, 100); // Brief pause before showing content
            });
        }

        /**
         * Complete boot sequence
         */
        completeBootSequence() {
            this.hideBootSequence();
            this.updateBootTimestamp();

            // Set localStorage values (same as installManager would set, using light theme)
            this.setInstallLocalStorage();

            // Show desktop wallpaper
            this.showDesktop();

            // Always show desktop UI (install is now handled by boot sequence)
            if (window.smashOS && window.smashOS.showDesktopUI) {
                window.smashOS.showDesktopUI();
                // Open default windows after desktop is shown
                setTimeout(() => {
                    if (window.smashOS.windowManager) {
                        window.smashOS.windowManager.openDefaultWindows();
                    }
                }, 500);
            }

            // Install manager is deactivated - boot sequence now handles installation
            // Uncomment below to re-enable install manager:
            /*
            const installCompleted = localStorage.getItem('smash_install_completed') === 'true';
            if (!installCompleted) {
                if (window.smashOS && window.smashOS.installManager) {
                    if (window.smashOS.installManager.shouldShowInstall()) {
                        window.smashOS.installManager.showInstallWizard();
                    }
                }
            }
            */
        }

        /**
         * Hide boot sequence and remove from DOM
         * Removes both #boot-sequence element and .boot-sequence section wrapper
         */
        hideBootSequence() {
            // Find by ID
            const bootContainer = document.getElementById('boot-sequence');

            if (bootContainer) {
                // Remove visible class to hide it
                bootContainer.classList.remove('visible');
                document.body.style.overflow = '';

                // Remove from DOM after a brief delay (for any transitions)
                setTimeout(() => {
                    this.removeBootSequenceFromDOM();
                }, 300);
            } else {
                // If container not found, try removing immediately
                this.removeBootSequenceFromDOM();
            }
        }

        /**
         * Setup DEL button handler
         */
        setupDelButton() {
            const delBtn = document.getElementById('boot-del-btn');
            if (delBtn) {
                delBtn.addEventListener('click', () => {
                    this.skipBoot();
                });
            }

            // Also setup ESC key and F4 key
            const keyHandler = (e) => {
                if (e.key === 'Escape' || e.keyCode === 27 || e.key === 'F4' || e.keyCode === 115) {
                    this.skipBoot();
                }
            };

            document.addEventListener('keydown', keyHandler);
            this.skipKeyHandler = keyHandler;
        }

        /**
         * Skip boot sequence
         */
        skipBoot() {
            // Remove event listeners
            if (this.skipKeyHandler) {
                document.removeEventListener('keydown', this.skipKeyHandler);
            }

            this.hideBootSequence();
            this.updateBootTimestamp();

            // Set localStorage values (same as installManager would set, using light theme)
            this.setInstallLocalStorage();

            // Show desktop
            this.showDesktop();

            // Always show desktop UI (install is now handled by boot sequence)
            if (window.smashOS && window.smashOS.showDesktopUI) {
                window.smashOS.showDesktopUI();
            }

            // Install manager is deactivated - boot sequence now handles installation
            // Uncomment below to re-enable install manager:
            /*
            if (window.smashOS && window.smashOS.installManager) {
                if (window.smashOS.installManager.shouldShowInstall()) {
                    window.smashOS.installManager.showInstallWizard();
                }
            }
            */
        }

        /**
         * Update boot timestamp
         */
        updateBootTimestamp() {
            localStorage.setItem('smash_last_boot_timestamp', Date.now().toString());
        }

        /**
         * Set localStorage values that installManager would set (using light theme)
         * This replaces the install wizard functionality
         */
        setInstallLocalStorage() {
            // Set install completed flag
            localStorage.setItem('smash_install_completed', 'true');
            
            // Set boot timestamp (already set by updateBootTimestamp, but ensure it's set)
            localStorage.setItem('smash_last_boot_timestamp', Date.now().toString());
            
            // Set theme preference to light (default)
            localStorage.setItem('smash_install_theme', 'light');
            localStorage.setItem('smash_theme_preference', 'light');
            
            // Note: smash_install_email is optional and not set here
            // It can be set later if needed
        }

        /**
         * Show desktop (after boot check completes)
         * Sets wallpaper but doesn't show UI elements - that's handled by showDesktopUI()
         */
        showDesktop() {
            const desktop = document.getElementById('desktop');
            if (desktop) {
                // Ensure background is set before showing desktop to prevent black flash
                const themePreference = localStorage.getItem('smash_theme_preference') || 'light';

                if (window.smashOSAssets) {
                    if (themePreference === 'dark') {
                        desktop.setAttribute('data-theme', 'dark');
                        desktop.style.setProperty('background-image', `url('${window.smashOSAssets.wallpaperDark}')`, 'important');
                    } else {
                        desktop.removeAttribute('data-theme');
                        desktop.style.setProperty('background-image', `url('${window.smashOSAssets.wallpaperLight}')`, 'important');
                    }
                    desktop.style.setProperty('background-size', 'cover', 'important');
                    desktop.style.setProperty('background-position', 'center', 'important');
                    desktop.style.setProperty('background-repeat', 'no-repeat', 'important');
                }

                // Remove black background but keep desktop hidden until wallpaper is confirmed loaded
                desktop.style.backgroundColor = '';
                // Don't show desktop yet - showDesktopUI() will handle that after wallpaper loads
            }
        }

        /**
         * Initialize boot manager
         */
        init(settings = {}) {
            this.bootEnabled = settings.enabled !== false;
            this.bootIntervalDays = settings.intervalDays || 1;

            // Remove boot sequence from DOM if localStorage indicates boot is already completed
            // and boot shouldn't be shown again
            const lastBootTimestamp = localStorage.getItem('smash_last_boot_timestamp');
            const installCompleted = localStorage.getItem('smash_install_completed') === 'true';
            const shouldShowBoot = this.shouldShowBoot();

            // If boot is completed and shouldn't be shown, remove it from DOM
            // Retry if element not found yet (DOM might not be ready)
            if (lastBootTimestamp && installCompleted && !shouldShowBoot) {
                const tryRemove = (attempts = 0) => {
                    const removed = this.removeBootSequenceFromDOM();
                    if (!removed && attempts < 10) {
                        // Element not found yet, retry after a short delay
                        setTimeout(() => tryRemove(attempts + 1), 100);
                    }
                };
                tryRemove();
            }
        }

        /**
         * Remove boot sequence elements from DOM
         * Removes both #boot-sequence element and .boot-sequence section wrapper
         * Also removes Shopify section wrapper (shopify-section-boot-sequence)
         * @returns {boolean} True if any elements were removed
         */
        removeBootSequenceFromDOM() {
            let removed = false;

            // Find by ID
            const bootContainer = document.getElementById('boot-sequence');
            if (bootContainer && bootContainer.parentElement) {
                bootContainer.remove();
                console.log('[BootManager] Boot sequence element (#boot-sequence) removed from DOM');
                removed = true;
            }

            // Find by class (Shopify section wrapper)
            const bootSection = document.querySelector('.boot-sequence');
            if (bootSection && bootSection.parentElement) {
                bootSection.remove();
                console.log('[BootManager] Boot sequence section (.boot-sequence) removed from DOM');
                removed = true;
            }

            // Also find Shopify section wrapper (shopify-section-{section-name})
            const shopifySection = document.getElementById('shopify-section-boot-sequence');
            if (shopifySection && shopifySection.parentElement) {
                shopifySection.remove();
                console.log('[BootManager] Shopify boot sequence section (shopify-section-boot-sequence) removed from DOM');
                removed = true;
            }

            return removed;
        }
    }

    // Export to global scope
    window.BootManager = BootManager;
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = BootManager;
}
